import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';

const BASE_URL = 'https://hanman.com';

export default function AboutPage() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩漫网',
    description: '韩漫网品牌介绍、口碑评价与联系方式。免费韩漫大全，好看的韩国漫画在线观看平台。',
    url: `${BASE_URL}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: BASE_URL,
    },
    about: {
      '@type': 'Organization',
      name: '韩漫网',
      description: '免费韩漫在线阅读平台',
      url: BASE_URL,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@hanman.com',
        availableLanguage: ['Chinese'],
      },
    },
  };

  return (
    <>
      <SEO
        title="关于我们_品牌介绍_联系方式-韩漫网"
        description="韩漫网品牌介绍，了解我们的口碑评价和联系方式。韩漫网是专注于免费韩漫大全与好看韩国漫画的韩漫在线观看平台，漫画网址首选。"
        keywords="关于韩漫网,韩漫网品牌介绍,韩漫联系方式,免费韩漫,韩国漫画平台"
        canonicalUrl={`${BASE_URL}/about`}
        ogType="website"
        schema={aboutSchema}
      />

      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4 text-center">
            关于韩漫网
          </h1>
          <p className="text-sm text-foreground-500 text-center mb-12 max-w-2xl mx-auto">
            致力于为华语读者提供最优质的免费韩漫在线阅读体验
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {[
              { icon: 'ri-global-line', title: '海量资源', desc: '汇集200+优质韩漫作品，覆盖12大分类板块，每日更新，让您畅享最全的韩国漫画资源。' },
              { icon: 'ri-shield-check-line', title: '免费阅读', desc: '所有漫画内容完全免费，无需注册付费，打开浏览器即可开始阅读心仪的韩漫作品。' },
              { icon: 'ri-device-line', title: '多端适配', desc: '完美适配电脑、平板和手机等各种设备，随时随地享受流畅的韩漫在线阅读体验。' },
            ].map((item, i) => (
              <div key={i} className="text-center p-6 rounded-xl bg-background-100">
                <div className="w-14 h-14 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-2xl text-primary-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mb-14">
            <h2 className="text-2xl font-bold font-heading text-foreground-950 mb-6 text-center">
              品牌故事
            </h2>
            <div className="bg-background-100 rounded-xl p-6 md:p-8">
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                韩漫网诞生于对韩国漫画的热爱。我们的创始团队发现，虽然韩国漫画在全球范围内越来越受欢迎，但华语读者往往难以找到高质量、免费的韩漫资源。于是，韩漫网应运而生。
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                从最初的几十部漫画开始，韩漫网不断扩展漫画库，现在已拥有超过200部优质韩漫作品，涵盖恋爱、奇幻、动作、搞笑、悬疑、BL、GL等12大分类。我们与专业翻译团队合作，确保每一部漫画都有高质量的简体中文翻译。
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed">
                韩漫网的使命很简单：让每一位华语读者都能轻松找到并阅读自己喜欢的韩国漫画。我们相信，好的漫画应该被更多人看到，这也是我们坚持免费模式的初心。未來，韩漫网将继续拓展漫画资源，优化阅读体验，成为华语圈最值得信赖的韩漫在线阅读平台。
              </p>
            </div>
          </div>

          <div className="mb-14">
            <h2 className="text-2xl font-bold font-heading text-foreground-950 mb-6 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { name: '漫画迷小王', rating: 5, text: '韩漫网的漫画资源太全了！我想看的韩漫几乎都能在这里找到，而且更新速度很快，强烈推荐！' },
                { name: '读者小李', rating: 5, text: '画质清晰，翻译质量很高，阅读体验非常流畅。最重要的是全部免费，真的很良心。' },
                { name: '学生小张', rating: 5, text: '作为学生党，免费韩漫网对我来说太友好了。每天下课追几话韩漫，已经成为我的日常习惯了。' },
                { name: '上班族陈姐', rating: 5, text: '手机上的阅读体验很好，通勤路上看看韩漫，放松心情。分类也很清楚，找漫画很方便。' },
                { name: '漫画家粉丝', rating: 4, text: '漫画种类丰富，从热门的到小众的都有。希望能有更多完结漫画上线，期待韩漫网越来越好！' },
                { name: '资深读者', rating: 5, text: '用了半年多了，非常稳定。漫画韩漫网址我只认这一个，推荐给了很多朋友，都说好用。' },
              ].map((review, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-5 flex flex-col gap-3">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }, (_, j) => (
                      <i
                        key={j}
                        className={`text-xs ${j < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed flex-1">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">&mdash; {review.name}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold font-heading text-foreground-950 mb-6 text-center">
              联系我们
            </h2>
            <div className="bg-background-100 rounded-xl p-6 md:p-8 max-w-2xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-600">contact@hanman.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-customer-service-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">在线客服</h4>
                    <p className="text-sm text-foreground-600">工作日 9:00 - 18:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-chat-3-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">意见反馈</h4>
                    <p className="text-sm text-foreground-600">欢迎通过邮件提出您的宝贵意见</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-team-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">商务合作</h4>
                    <p className="text-sm text-foreground-600">business@hanman.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}